import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {
    HeaderItem,
    NavItem,
    NavTarget,
    ScaffoldApp,
    ScaffoldByUserTemplate,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {
    findScaffoldApp,
    getDefaultScaffoldAppId,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";

export function resolvePageIdFromNavTarget(target: NavTarget): Uuid | null {
    if (target.type === "page") {
        return target.pageId;
    }
    return null;
}

function resolveExternalHref(args: {target: NavTarget; workspaceId: Uuid}): string | null {
    const {target, workspaceId} = args;
    if (target.type === "absolutePath") {
        return target.pathTemplate.replaceAll("$workspaceId", workspaceId);
    }
    return null;
}

function collectPageKeysFromNavItems(navItems: Array<NavItem>, pageKeys: Set<string>): void {
    for (const item of navItems) {
        if (item.type !== "link") {
            continue;
        }
        const pageId = resolvePageIdFromNavTarget(item.target);
        if (pageId !== null) {
            pageKeys.add(pageId);
        }
        if (item.nest !== undefined && item.nest !== null && item.nest.kind === "pages") {
            for (const nestedPage of item.nest.pages) {
                pageKeys.add(nestedPage.pageId);
            }
        }
    }
}

function collectPageKeysFromHeader(header: Array<HeaderItem>, pageKeys: Set<string>): void {
    for (const item of header) {
        if (item.type !== "settings" && item.type !== "teamSpace") {
            continue;
        }
        for (const link of item.items) {
            const pageId = resolvePageIdFromNavTarget(link.target);
            if (pageId !== null) {
                pageKeys.add(pageId);
            }
        }
    }
}

export function isPageKeyInScaffoldSidebar(roleChrome: ScaffoldByUserTemplate, pageKey: string): boolean {
    const pageKeys = new Set<string>();
    for (const app of roleChrome.sidebar) {
        if (app.modules !== undefined && app.modules.length > 0) {
            collectPageKeysFromNavItems(app.modules, pageKeys);
        }
        if (app.navigation !== undefined) {
            const pageId = resolvePageIdFromNavTarget(app.navigation);
            if (pageId !== null) {
                pageKeys.add(pageId);
            }
        }
    }
    return pageKeys.has(pageKey);
}

export function isPageKeyInScaffoldHeader(roleChrome: ScaffoldByUserTemplate, pageKey: string): boolean {
    const pageKeys = new Set<string>();
    collectPageKeysFromHeader(roleChrome.header, pageKeys);
    return pageKeys.has(pageKey);
}

export function resolveFirstPageKeyInNav(nav: Array<NavItem>): Uuid | undefined {
    for (const item of nav) {
        if (item.type !== "link") {
            continue;
        }
        const pageId = resolvePageIdFromNavTarget(item.target);
        if (pageId !== null) {
            return pageId;
        }
        if (item.nest !== undefined && item.nest !== null && item.nest.kind === "pages") {
            const firstNestedPage = item.nest.pages[0];
            if (firstNestedPage !== undefined) {
                return firstNestedPage.pageId;
            }
        }
    }
    return undefined;
}

export function resolveDefaultPageKeyForApp(app: ScaffoldApp): Uuid | undefined {
    if (app.modules !== undefined && app.modules.length > 0) {
        return resolveFirstPageKeyInNav(app.modules);
    }
    if (app.navigation !== undefined) {
        return resolvePageIdFromNavTarget(app.navigation) ?? undefined;
    }
    return undefined;
}

/**
 * An app is active when the current page belongs to it:
 * modules (when present) or the app's own page target (when it has no modules).
 */
export function doesScaffoldAppContainPageKey(app: ScaffoldApp, pageKey: string): boolean {
    if (app.modules !== undefined && app.modules.length > 0) {
        const pageKeys = new Set<string>();
        collectPageKeysFromNavItems(app.modules, pageKeys);
        return pageKeys.has(pageKey);
    }
    if (app.navigation === undefined) {
        return false;
    }
    return resolvePageIdFromNavTarget(app.navigation) === pageKey;
}

export function resolveAppIdForPageKey(args: {
    apps: Array<ScaffoldApp>;
    pageKey: string;
    fallbackAppId: Uuid;
}): Uuid {
    for (const app of args.apps) {
        if (doesScaffoldAppContainPageKey(app, args.pageKey) === true) {
            return app.id;
        }
    }
    return args.fallbackAppId;
}

/** First sidebar page for a role — used only to backfill missing `entryPageId`. */
export function resolveFirstSidebarPageKey(roleChrome: Pick<ScaffoldByUserTemplate, "sidebar">): Uuid | undefined {
    const appId = getDefaultScaffoldAppId(roleChrome);
    const app = findScaffoldApp(roleChrome.sidebar, appId);
    const pageKeyFromApp = app === undefined ? undefined : resolveDefaultPageKeyForApp(app);
    if (pageKeyFromApp !== undefined) {
        return pageKeyFromApp;
    }
    for (const candidateApp of roleChrome.sidebar) {
        const pageKey = resolveDefaultPageKeyForApp(candidateApp);
        if (pageKey !== undefined) {
            return pageKey;
        }
    }
    return undefined;
}

export function resolveDefaultScaffoldEntry(roleChrome: ScaffoldByUserTemplate): {appId: Uuid; pageKey: Uuid} {
    const pageKey = roleChrome.entryPageId;
    return {
        appId: resolveAppIdForPageKey({
            apps: roleChrome.sidebar,
            pageKey: pageKey,
            fallbackAppId: getDefaultScaffoldAppId(roleChrome),
        }),
        pageKey: pageKey,
    };
}

export type ScaffoldAppRailTarget =
    | {
          kind: "configPage";
          pageKey: Uuid;
          appId: Uuid;
      }
    | {
          kind: "external";
          href: string;
          appId: Uuid;
      };

export function resolveScaffoldAppRailTarget(args: {
    app: ScaffoldApp;
    workspaceId: Uuid;
}): ScaffoldAppRailTarget {
    if (args.app.modules !== undefined && args.app.modules.length > 0) {
        const pageKey = resolveDefaultPageKeyForApp(args.app);
        if (pageKey !== undefined) {
            return {
                kind: "configPage",
                pageKey: pageKey,
                appId: args.app.id,
            };
        }
    }
    if (args.app.navigation !== undefined) {
        const pageKey = resolvePageIdFromNavTarget(args.app.navigation);
        if (pageKey !== null) {
            return {
                kind: "configPage",
                pageKey: pageKey,
                appId: args.app.id,
            };
        }
        const externalHref = resolveExternalHref({
            target: args.app.navigation,
            workspaceId: args.workspaceId,
        });
        if (externalHref !== null) {
            return {
                kind: "external",
                href: externalHref,
                appId: args.app.id,
            };
        }
    }
    const fallbackPageKey = resolveDefaultPageKeyForApp(args.app);
    return {
        kind: "configPage",
        pageKey: fallbackPageKey ?? args.app.id,
        appId: args.app.id,
    };
}
